/**
 * Soludos cart drawer extras - replaces the Rebuy Smart Cart behaviours on the native
 * Broadcast cart drawer. Loaded by sections/cart-drawer.liquid.
 *
 * 1. Free gift with purchase (Theme settings > Cart > Free gift with purchase)
 *    Uses Broadcast's built-in GWP hook: when window.GWP_CART_DRAWER_CONFIG exists, the theme
 *    fires `theme:cart:change` (detail.source = 'theme-add-to-cart' | 'theme-update-cart')
 *    after every add / quantity change and waits. We add or remove the gift, then set
 *    PresidioPlus.gwpProcessed = true and fire `theme:cart:refresh` so the drawer redraws.
 *    Rules: one gift, qty 1, only while the cart holds a paid item (and meets the minimum
 *    spend); if the customer removes it, it is added back automatically on the next cart
 *    change as long as the cart still qualifies (2026-10-01 - previously stuck removed for
 *    the rest of that cart; merchant wants it reappear whenever the customer is eligible).
 * 2. <cart-spend-tiers> - keeps the two-stage progress bar current after every cart change.
 * 3. Accessibility - drawer content is inert while closed; focus returns to the opener.
 * 4. window.SoludosCart.refresh() - for code that adds to cart outside the theme's own
 *    product forms (e.g. assets/bundle-pairs.js), which previously relied on Rebuy.
 * 5. <cart-recommendations> - "Have you considered" cross-sell (2026-10-05). Shopify-native,
 *    not Rebuy: fetches /recommendations/products.json (the same endpoint and section the
 *    PDP's own recommendation blocks use - see assets/related-products.js) seeded from the
 *    most-recently-added cart line, filters out anything already in the cart, and hides
 *    itself when nothing is left to show. 2026-10-07: horizontal cards with an inline size
 *    select + ADD button (Figma node 6291:618). 2026-10-13: prev / next arrows.
 */
(function () {
  if (window.SoludosCart) return;

  // Only now that this handler exists does the theme start deferring cart redraws to it
  if (window.SOLUDOS_GIFT_CONFIG && window.SOLUDOS_GIFT_CONFIG.variantId) {
    window.GWP_CART_DRAWER_CONFIG = window.SOLUDOS_GIFT_CONFIG;
  }

  const cartJsUrl = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js';
  const cartAddUrl = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js';
  const cartChangeUrl = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/change.js';
  const jsonHeaders = {'Content-Type': 'application/json', Accept: 'application/json'};

  const getCart = () => fetch(cartJsUrl(), {headers: {Accept: 'application/json'}}).then((r) => r.json());
  const changeLine = (key, quantity) =>
    fetch(cartChangeUrl(), {method: 'POST', headers: jsonHeaders, body: JSON.stringify({id: key, quantity})}).then((r) => r.json());
  const addItem = (id, properties) =>
    fetch(cartAddUrl(), {method: 'POST', headers: jsonHeaders, body: JSON.stringify({items: [{id, quantity: 1, properties}]})}).then((r) => r.json());

  /* ---------------------------------------------------------------- 1. Free gift */

  // 2026-10-01: removal used to be remembered per cart (soludos:gwp / soludos:gwp:declined in
  // localStorage) so a customer who removed the gift never saw it added back to that cart. The
  // merchant wants the opposite - removing it is treated the same as any other way the gift line
  // can go missing (cart cleared elsewhere, another tab): the next reconcile adds it straight
  // back as long as the cart still qualifies. No "declined" state is kept any more.

  let giftQueue = Promise.resolve(false);

  /** Bring the cart in line with the gift rules. Resolves true if the cart was changed. */
  function reconcileGift() {
    const config = window.GWP_CART_DRAWER_CONFIG;
    if (!config || !config.variantId) return Promise.resolve(false);

    giftQueue = giftQueue.then(async () => {
      const cart = await getCart();
      const giftId = Number(config.variantId);
      const giftLines = cart.items.filter((item) => item.variant_id === giftId);
      const paidLines = cart.items.filter((item) => item.variant_id !== giftId);
      const paidSubtotal = paidLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifies = paidLines.length > 0 && paidSubtotal >= (Number(config.minSubtotal) || 0);
      let changed = false;

      if (!qualifies) {
        for (const line of giftLines) {
          await changeLine(line.key, 0);
          changed = true;
        }
      } else if (giftLines.length === 0) {
        // Eligible but the gift line is missing - whether it was just removed by the customer,
        // the cart was cleared elsewhere, or this is another tab, it's simply added back
        const response = await addItem(giftId, {_gwp: '1'});
        if (!response.status) changed = true;
      } else {
        const [first, ...extra] = giftLines;
        for (const line of extra) {
          await changeLine(line.key, 0);
          changed = true;
        }
        if (first.quantity !== 1) {
          await changeLine(first.key, 1);
          changed = true;
        }
      }

      return changed;
    }).catch((error) => {
      console.warn('[soludos-cart] gift check failed', error);
      return false;
    });

    return giftQueue;
  }

  function releaseDrawer() {
    window.PresidioPlus = window.PresidioPlus || {};
    window.PresidioPlus.gwpProcessed = true;
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
  }

  // The theme hands over after its own add / update and waits for theme:cart:refresh.
  // Events without a source are the theme's post-render notices - ignore them (no loop).
  document.addEventListener('theme:cart:change', (event) => {
    const source = event.detail && event.detail.source;
    if (source !== 'theme-add-to-cart' && source !== 'theme-update-cart') return;
    if (!window.GWP_CART_DRAWER_CONFIG) return;
    reconcileGift().finally(releaseDrawer);
  });

  // Carts built elsewhere (another tab, Shop app, a returning visitor): check when the drawer opens
  document.addEventListener('theme:cart-drawer:open', () => {
    reconcileGift().then((changed) => {
      if (changed) releaseDrawer();
    });
  });

  // Cart page (no drawer): check once on load and reload if the gift had to change
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('[data-cart-page]') || !window.GWP_CART_DRAWER_CONFIG) return;
    reconcileGift().then((changed) => {
      if (changed) window.location.reload();
    });
  });

  /* ---------------------------------------------------------------- 2. Spend tiers */

  class CartSpendTiers extends HTMLElement {
    connectedCallback() {
      try {
        this.tiers = JSON.parse(this.dataset.tiers || '[]');
      } catch (e) {
        this.tiers = [];
      }
      this.update = this.update.bind(this);
      document.addEventListener('theme:product:added', this.update);
      document.addEventListener('theme:cart-drawer:open', this.update);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:product:added', this.update);
      document.removeEventListener('theme:cart-drawer:open', this.update);
    }

    update() {
      getCart()
        .then((cart) => this.render(cart.items_subtotal_price))
        .catch(() => {});
    }

    formatMoney(cents) {
      if (window.theme && window.theme.formatMoney) {
        return window.theme.formatMoney(cents, window.theme.moneyFormat).replace('.00', '');
      }
      return '$' + Math.round(cents / 100);
    }

    render(subtotal) {
      if (!this.tiers.length) return;
      const rate = (window.Shopify && window.Shopify.currency && Number(window.Shopify.currency.rate)) || 1;
      const amounts = this.tiers.map((tier) => Math.round(tier.amount * rate));
      const max = amounts[amounts.length - 1];
      const percent = max > 0 ? Math.min((subtotal / max) * 100, 100) : 100;

      const fill = this.querySelector('[data-spend-tiers-fill]');
      if (fill) fill.style.width = `${percent}%`;

      amounts.forEach((amount, index) => {
        const reached = subtotal >= amount;
        this.querySelector(`[data-spend-tiers-marker="${index}"]`)?.classList.toggle('is-reached', reached);
        this.querySelector(`[data-spend-tiers-label="${index}"]`)?.classList.toggle('is-reached', reached);
      });

      const nextIndex = amounts.findIndex((amount) => subtotal < amount);
      const message = this.querySelector('[data-spend-tiers-message]');
      if (!message) return;

      if (nextIndex === -1) {
        message.textContent = this.dataset.completeMessage || '';
      } else {
        message.textContent = (this.dataset.remainingMessage || '')
          .replace('||amount||', this.formatMoney(amounts[nextIndex] - subtotal))
          .replace('||reward||', this.tiers[nextIndex].label);
      }
    }
  }

  if (!customElements.get('cart-spend-tiers')) {
    customElements.define('cart-spend-tiers', CartSpendTiers);
  }

  /* ---------------------------------------------------------------- 3. Accessibility */

  let lastFocus = null;

  function setDrawerInert(isInert) {
    if (window.Shopify && window.Shopify.designMode) return; // keep the editor usable
    const inner = document.querySelector('#cart-drawer [data-cart-drawer-inner]');
    if (!inner) return;
    if (isInert) inner.setAttribute('inert', '');
    else inner.removeAttribute('inert');
  }

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && !drawer.classList.contains('is-open')) setDrawerInert(true);
  });

  document.addEventListener('theme:cart-drawer:open', () => {
    const active = document.activeElement;
    lastFocus = active && active !== document.body && !active.closest('#cart-drawer') ? active : lastFocus;
    setDrawerInert(false);
  });

  document.addEventListener('theme:cart-drawer:close', () => {
    setDrawerInert(true);
    window.setTimeout(() => {
      const focusLost = !document.activeElement || document.activeElement === document.body;
      if (focusLost && lastFocus && document.contains(lastFocus)) lastFocus.focus();
    }, 50);
  });

  /* ---------------------------------------------------------------- 5. Have you considered */

  // 2026-10-07: cards redesigned to the updated Figma (node 6291:618, "horizontal cards"):
  // 300px-wide card with a full-height image column, title / colour / price, and an inline
  // size <select> + ADD button so the customer can add straight from the drawer. Variant data
  // (sizes + availability) isn't in the recommendations markup, so each card fetches
  // /products/<handle>.js once (cached) and fills its <select> from that. ADD posts to
  // /cart/add.js and then calls window.SoludosCart.refresh() - the same hand-off
  // assets/bundle-pairs.js uses - so the free-gift rules, spend tiers and the drawer redraw.
  // A thin scroll indicator under the row replaces the native scrollbar.
  const REC_TEXT = {
    size: 'Size',
    add: 'ADD',
    added: 'ADDED',
    soldOut: 'sold out',
    pickSize: 'Please select a size.',
    error: "Sorry, we couldn't add that item. Please try again.",
  };

  const productCache = new Map();
  const getProduct = (handle) => {
    if (!productCache.has(handle)) {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      productCache.set(
        handle,
        fetch(`${root}products/${encodeURIComponent(handle)}.js`, {headers: {Accept: 'application/json'}})
          .then((response) => {
            if (!response.ok) throw new Error(`product ${response.status}`);
            return response.json();
          })
          .catch((error) => {
            productCache.delete(handle);
            throw error;
          })
      );
    }
    return productCache.get(handle);
  };

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.limit = Number(this.dataset.limit) || 4;
      this.intent = this.dataset.intent || 'complementary';
      this.sectionId = this.dataset.sectionId || 'api-product-recommendation';
      this.grid = this.querySelector('[data-cart-recommendations-grid]');
      this.track = this.querySelector('[data-cart-recommendations-track]');
      this.thumb = this.querySelector('[data-cart-recommendations-thumb]');
      this.prevButton = this.querySelector('[data-cart-recommendations-prev]');
      this.nextButton = this.querySelector('[data-cart-recommendations-next]');
      this.updateToken = 0;
      this.update = this.update.bind(this);
      this.updateIndicator = this.updateIndicator.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.onGridChange = this.onGridChange.bind(this);
      this.onPrev = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
      document.addEventListener('theme:cart:refresh', this.update);
      document.addEventListener('theme:product:added', this.update);
      document.addEventListener('theme:cart-drawer:open', this.update);
      document.addEventListener('theme:cart-drawer:open', this.updateIndicator);
      if (this.grid) {
        this.grid.addEventListener('click', this.onGridClick);
        this.grid.addEventListener('change', this.onGridChange);
        this.grid.addEventListener('scroll', this.updateIndicator, {passive: true});
        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.updateIndicator);
          this.resizeObserver.observe(this.grid);
        }
      }
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:refresh', this.update);
      document.removeEventListener('theme:product:added', this.update);
      document.removeEventListener('theme:cart-drawer:open', this.update);
      document.removeEventListener('theme:cart-drawer:open', this.updateIndicator);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    }

    // 2026-10-06: fetch + build cards for a single intent. Split out of update() so update()
    // can retry with a different intent when the first one comes back empty (see below) -
    // Shopify's "complementary" recommendations are driven by sales/co-purchase data (plus any
    // manual curation in Search & Discovery), so a newer or low-volume product can have none at
    // all even though "related" (content/tag based) almost always returns something.
    async fetchCards(intent, seed, cartHandles) {
      const recUrl = (window.theme && window.theme.routes && window.theme.routes.product_recommendations_url) || '/recommendations/products.json';
      const url = `${recUrl}?section_id=${this.sectionId}&limit=${this.limit + cartHandles.size}&product_id=${seed.product_id}&intent=${intent}`;

      const response = await fetch(url, {headers: {Accept: 'text/html'}});
      const html = await response.text();
      const template = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-api-related-template]');
      if (!template) return [];

      // 2026-10-05: build plain cards from the fetched data instead of re-injecting the
      // fetched <grid-item>/<hover-images> markup wholesale. That markup depends on this cart
      // drawer's own staggered entrance-animation plumbing (data-animation="cart-items-fade"),
      // which never reveals content inserted after the drawer's own reveal pass, so cards
      // rendered with correct markup but invisible text. Only the image, title, colour, price
      // and product URL are read from it; the shared global .price/.new-price/.old-price
      // styling is reused for the price line.
      //
      // [data-api-related-template] is itself a <template>, so its markup lives in .content,
      // not as light-DOM children - copy template.innerHTML into a plain <div> and query that
      // (same workaround as assets/related-products.js).
      const container = document.createElement('div');
      container.innerHTML = template.innerHTML;

      const cards = [];
      container.querySelectorAll('[data-grid-item]').forEach((item) => {
        if (cards.length >= this.limit) return;

        const productUrl = item.getAttribute('data-url') || '';
        const handle = productUrl.split('/products/')[1]?.split(/[?#]/)[0];
        if (!handle || cartHandles.has(handle)) return; // no handle, or already in cart

        // Don't require :not(.is-loading) on the image - that class is only cleared by the
        // theme's lazy-load JS once an <img> fires load in the live page; it's still present
        // on every image in this never-hydrated fragment even though src/srcset are real.
        const img = item.querySelector('.product-item__bg img.fit-cover');
        const titleEl = item.querySelector('.product-item__title');
        const priceHolder = item.querySelector('.product-item__price__holder');
        if (!img || !titleEl) return;

        const title = titleEl.textContent.trim();
        const colorEl = priceHolder && priceHolder.querySelector('.product-item__cutline');
        const priceEl = priceHolder && priceHolder.querySelector('.product-item__price');

        const card = document.createElement('div');
        card.className = 'cart-recommendations__card';
        card.dataset.handle = handle;

        // Image column
        const imageLink = document.createElement('a');
        imageLink.className = 'cart-recommendations__card-image';
        imageLink.href = productUrl;
        imageLink.tabIndex = -1;
        imageLink.setAttribute('aria-hidden', 'true');
        const cardImg = document.createElement('img');
        if (img.getAttribute('srcset')) cardImg.srcset = img.getAttribute('srcset');
        cardImg.src = img.getAttribute('src') || '';
        cardImg.sizes = '92px';
        cardImg.alt = '';
        cardImg.loading = 'lazy';
        cardImg.width = 92;
        cardImg.height = 115;
        imageLink.appendChild(cardImg);
        card.appendChild(imageLink);

        // Content column
        const body = document.createElement('div');
        body.className = 'cart-recommendations__card-body';

        const info = document.createElement('div');
        info.className = 'cart-recommendations__card-info';

        const titleLink = document.createElement('a');
        titleLink.className = 'cart-recommendations__card-title';
        titleLink.href = productUrl;
        titleLink.textContent = title;
        info.appendChild(titleLink);

        if (colorEl && colorEl.textContent.trim()) {
          const colorSpan = document.createElement('span');
          colorSpan.className = 'cart-recommendations__card-color';
          colorSpan.textContent = colorEl.textContent.trim();
          info.appendChild(colorSpan);
        }

        if (priceEl) {
          const priceSpan = document.createElement('span');
          priceSpan.className = 'cart-recommendations__card-price';
          priceSpan.innerHTML = priceEl.innerHTML;
          info.appendChild(priceSpan);
        }
        body.appendChild(info);

        // Size select + ADD (select is filled in by hydrate() once the product JSON loads)
        const actions = document.createElement('div');
        actions.className = 'cart-recommendations__actions';

        const select = document.createElement('select');
        select.className = 'cart-recommendations__size is-placeholder';
        select.setAttribute('data-rec-size', '');
        select.setAttribute('aria-label', `Size for ${title}`);
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = REC_TEXT.size;
        select.appendChild(placeholder);
        actions.appendChild(select);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-recommendations__add';
        button.setAttribute('data-rec-add', '');
        button.setAttribute('aria-label', `Add ${title} to cart`);
        button.textContent = REC_TEXT.add;
        actions.appendChild(button);
        body.appendChild(actions);

        const error = document.createElement('p');
        error.className = 'cart-recommendations__error';
        error.setAttribute('data-rec-error', '');
        error.setAttribute('role', 'alert');
        error.hidden = true;
        body.appendChild(error);

        card.appendChild(body);
        cards.push(card);
      });

      return cards;
    }

    /** Fill a card's size <select> from /products/<handle>.js (sold-out sizes shown but disabled). */
    async hydrate(card) {
      const select = card.querySelector('[data-rec-size]');
      const button = card.querySelector('[data-rec-add]');
      const actions = card.querySelector('.cart-recommendations__actions');
      if (!select || !button) return;

      try {
        const product = await getProduct(card.dataset.handle);
        const variants = product.variants || [];
        if (!variants.length) throw new Error('no variants');

        // Single-variant product (no real choice): hide the select, ADD adds that variant
        if (variants.length === 1) {
          select.hidden = true;
          button.dataset.variantId = variants[0].id;
          card.classList.add('is-single-variant');
          if (!variants[0].available) button.disabled = true;
          return;
        }

        const names = (product.options || []).map((option) => (typeof option === 'string' ? option : option.name));
        const sizeIndex = names.findIndex((name) => /size/i.test(name));
        let labels = variants.map((variant) => (sizeIndex > -1 ? variant.options[sizeIndex] : variant.title));
        if (new Set(labels).size !== labels.length) labels = variants.map((variant) => variant.title);

        variants.forEach((variant, index) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.disabled = !variant.available;
          option.textContent = variant.available ? labels[index] : `${labels[index]} - ${REC_TEXT.soldOut}`;
          select.appendChild(option);
        });

        if (!variants.some((variant) => variant.available)) button.disabled = true;
      } catch (error) {
        // No variant data: drop the controls, the title/image still link to the product page
        if (actions) actions.hidden = true;
      }
    }

    onGridChange(event) {
      const select = event.target.closest('[data-rec-size]');
      if (!select) return;
      select.classList.toggle('is-placeholder', !select.value);
      select.classList.remove('is-invalid');
      select.removeAttribute('aria-invalid');
      const error = select.closest('.cart-recommendations__card')?.querySelector('[data-rec-error]');
      if (error) error.hidden = true;
    }

    onGridClick(event) {
      const button = event.target.closest('[data-rec-add]');
      if (button) this.addToCart(button);
    }

    async addToCart(button) {
      const card = button.closest('.cart-recommendations__card');
      const select = card.querySelector('[data-rec-size]');
      const error = card.querySelector('[data-rec-error]');
      const variantId = select.hidden ? button.dataset.variantId : select.value;

      if (!variantId) {
        select.classList.add('is-invalid');
        select.setAttribute('aria-invalid', 'true');
        error.textContent = REC_TEXT.pickSize;
        error.hidden = false;
        select.focus();
        return;
      }
      if (button.disabled) return;

      button.disabled = true;
      error.hidden = true;
      try {
        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({items: [{id: Number(variantId), quantity: 1}]}),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || REC_TEXT.error);

        button.textContent = REC_TEXT.added;
        button.classList.add('is-added');
        window.setTimeout(() => {
          button.textContent = REC_TEXT.add;
          button.classList.remove('is-added');
          button.disabled = false;
        }, 2000);

        // Same hand-off as assets/bundle-pairs.js: gift rules, tiers, redraw, keep drawer open
        if (window.SoludosCart) await window.SoludosCart.refresh();
      } catch (err) {
        error.textContent = err && err.message ? err.message : REC_TEXT.error;
        error.hidden = false;
        button.disabled = false;
      }
    }

    /** Scroll the row by one card (+ gap) in the given direction (-1 = back, 1 = forward). */
    scrollByCard(direction) {
      if (!this.grid) return;
      const card = this.grid.querySelector('.cart-recommendations__card');
      if (!card) return;
      const gap = parseFloat(window.getComputedStyle(this.grid).columnGap) || 0;
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.grid.scrollBy({left: direction * (card.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth'});
    }

    /** Size/position the scroll indicator and show/hide the prev/next arrows (all hidden when everything fits). */
    updateIndicator() {
      if (!this.grid || !this.track || !this.thumb) return;
      const {clientWidth, scrollWidth, scrollLeft} = this.grid;
      if (!clientWidth || scrollWidth <= clientWidth + 1) {
        this.track.hidden = true;
        if (this.prevButton) this.prevButton.hidden = true;
        if (this.nextButton) this.nextButton.hidden = true;
        return;
      }
      this.track.hidden = false;
      if (this.prevButton) this.prevButton.hidden = scrollLeft <= 2;
      if (this.nextButton) this.nextButton.hidden = scrollLeft >= scrollWidth - clientWidth - 2;
      const ratio = clientWidth / scrollWidth;
      const maxScroll = scrollWidth - clientWidth;
      this.thumb.style.width = `${ratio * 100}%`;
      this.thumb.style.left = `${(scrollLeft / maxScroll) * (1 - ratio) * 100}%`;
    }

    async update() {
      if (!this.grid) return;
      const token = ++this.updateToken;

      try {
        const cart = await getCart();
        if (!cart.items.length) {
          this.hidden = true;
          return;
        }

        // Seed on the most recently added line - cart.js lists items in cart order, newest last
        const seed = cart.items[cart.items.length - 1];
        const cartHandles = new Set(cart.items.map((item) => item.handle));

        let cards = await this.fetchCards(this.intent, seed, cartHandles);

        // 2026-10-06: fall back to "related" when the configured intent (normally
        // "complementary") comes back empty. Complementary recommendations need sales/
        // co-purchase history (or a manual Search & Discovery rule) behind them, so a newer or
        // low-volume product can have zero even though it's perfectly findable by "related"
        // (same type/tags). Reported live on "Women's Mary Jane Espadrille | Woven Color |
        // Dolphin Blue".
        const fallbackIntent = 'related';
        if (cards.length === 0 && this.intent !== fallbackIntent) {
          cards = await this.fetchCards(fallbackIntent, seed, cartHandles);
        }

        // A newer update() started while this one was fetching - let that one render
        if (token !== this.updateToken) return;

        if (cards.length === 0) {
          this.hidden = true;
          return;
        }

        this.grid.innerHTML = '';
        cards.forEach((card) => this.grid.appendChild(card));
        this.grid.scrollLeft = 0;
        this.hidden = false;
        cards.forEach((card) => this.hydrate(card));
        window.requestAnimationFrame(this.updateIndicator);
      } catch (error) {
        console.warn('[soludos-cart] recommendations failed', error);
        this.hidden = true;
      }
    }
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }

  /* ---------------------------------------------------------------- 6. Public hook */

  window.SoludosCart = {
    reconcileGift,
    /** Call after adding to cart outside the theme's product forms: applies the gift rules, redraws and opens the drawer. */
    refresh() {
      return reconcileGift().then(() => {
        releaseDrawer();
        document.querySelector('cart-drawer')?.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
      });
    },
  };
})();
